/**
 * Discharge-snippet ("Save as template" / "Use template") API client —
 * DISCHARGE-TEMPLATES Phase 1b Slice 1, FE-1. Binds VERBATIM to the frozen
 * `## API CONTRACT` §2 of
 * `state/epics/DISCHARGE-TEMPLATES/plan-1b/build-plan-1b-slice1.md`.
 *
 * Three admission-scoped routes (§2.4-2.6), all under the SAME
 * `require_admission_enabled` gate as `admissionDischargeApi.js` (§2.1):
 *   GET    /api/organisation/admissions/{id}/discharge-snippets?field=<key>
 *   POST   /api/organisation/admissions/{id}/discharge-snippets
 *   DELETE /api/organisation/admissions/{id}/discharge-snippets/{snippet_id}?field=<key>
 *
 * §2.1 — these calls sit under the same `BASE` SHAPE as
 * `admissionDischargeApi.js:33`, but this is a deliberately SEPARATE module
 * (own `BASE` constant, not an import) so `admissionDischargeApi.js` keeps
 * exactly one owner.
 *
 * Never a `doctor_member_id` (or any member id) on the wire from the FE
 * (§2.1) — the server resolves the owning doctor from the admission row.
 *
 * Errors: `detail: {code, message, ...extras}` via the shipped
 * `DischargeServiceError` family (§2.7) — codes are mapped to ratified copy
 * below; `detail.message` is NEVER read (backend stays UI-agnostic, same
 * rule as `admissionDischargeApi.js`'s `parseDischargeSummaryError`).
 *
 * bk-1177 (§2.7 last row): the ROUTE-LEVEL `require_permission` 403 is a
 * BARE STRING (`detail: "Permission '...' is required. Your role '...'
 * does not have it."`), not `{code}` — it leaks the caller's role + the
 * internal slug name. `parseSnippetError` below keys its 403 handling off
 * the HTTP STATUS, never off `detail` shape/text, so that sentence is
 * NEVER rendered — both the bare-string route-level refusal and the
 * object-shaped `NOT_PERMITTED` (service-raised, DELETE-only, §2.6) land
 * on the SAME ratified copy.
 *
 * RATIFIED (business stakeholder verdict, `build-1b/copy-ratified-slice1.md`,
 * 2026-09-17): `SNIPPET_ERROR_COPY` below carries the FINAL strings for §A
 * #1-#6 verbatim. `DOCTOR_REQUIRED` reuses the ACTUAL shipped
 * `DOCTOR_REQUIRED_COPY` (single source, not re-typed).
 */

import apiClient from './apiClient';
import { DOCTOR_REQUIRED_COPY, snippetLimitReachedCopy } from '../../constants/admission';

const BASE = '/api/organisation/admissions';
const enc = encodeURIComponent;

/** Machine error codes the three snippet routes raise (§2.7). */
export const SNIPPET_ERROR_CODE = {
  UNKNOWN_FIELD_KEY: 'UNKNOWN_FIELD_KEY',
  SNIPPETS_NOT_SUPPORTED: 'SNIPPETS_NOT_SUPPORTED',
  FIELD_TOO_LONG: 'FIELD_TOO_LONG',
  DOCTOR_REQUIRED: 'DOCTOR_REQUIRED',
  SNIPPET_LIMIT_REACHED: 'SNIPPET_LIMIT_REACHED',
  SNIPPET_NOT_FOUND: 'SNIPPET_NOT_FOUND',
  NOT_PERMITTED: 'NOT_PERMITTED',
};

/**
 * Code -> ratified copy (see the module-doc ASSUMPTION above).
 * `DOCTOR_REQUIRED` reuses the SHIPPED `DOCTOR_REQUIRED_COPY` verbatim
 * (owner ruling H, single source, `constants/admission.js`) — never
 * re-typed. `FIELD_TOO_LONG` / `SNIPPET_LIMIT_REACHED` are templated with
 * the server's `limit` extra (see the helpers below); this map holds only
 * their no-limit fallback sentence.
 */
// Ratified copy (copy-ratified-slice1.md §A #1-#6) — #1/#2/#3/#5 keep,
// #4/#6 updated. `SNIPPET_LIMIT_REACHED`'s sentence is now the ONE shared
// `snippetLimitReachedCopy` (`constants/admission.js`) — never re-typed
// here, so this route and the FE's own pre-emptive "Save as template"
// disabled reason (`SummaryFields.jsx`) can never disagree.
const SNIPPET_ERROR_COPY = {
  [SNIPPET_ERROR_CODE.UNKNOWN_FIELD_KEY]: "This field is not part of your organisation's discharge summary.",
  [SNIPPET_ERROR_CODE.SNIPPETS_NOT_SUPPORTED]: 'Templates are not available for this field.',
  [SNIPPET_ERROR_CODE.FIELD_TOO_LONG]: 'This entry is too long. Please shorten it.',
  [SNIPPET_ERROR_CODE.DOCTOR_REQUIRED]: DOCTOR_REQUIRED_COPY,
  [SNIPPET_ERROR_CODE.SNIPPET_LIMIT_REACHED]: snippetLimitReachedCopy(),
  [SNIPPET_ERROR_CODE.SNIPPET_NOT_FOUND]: 'This template no longer exists. It may already have been deleted.',
  [SNIPPET_ERROR_CODE.NOT_PERMITTED]: 'You do not have permission to save or delete discharge templates.',
};

/** §2.7: `title` > 80 -> `key:"title"`; `text` > `max_length` -> `key:<field>`. */
function fieldTooLongCopy(extras) {
  const { key, limit } = extras || {};
  if (limit == null) return SNIPPET_ERROR_COPY[SNIPPET_ERROR_CODE.FIELD_TOO_LONG];
  return key === 'title'
    ? `Title is too long. Please keep it under ${limit} characters.`
    : `This entry is too long. Please keep it under ${limit} characters.`;
}

/** §2.7: `SNIPPET_LIMIT_REACHED` carries `{limit: 50}`. */
function snippetLimitCopy(extras) {
  return snippetLimitReachedCopy(extras && extras.limit);
}

/**
 * §2.7 error translation for the three snippet routes. `detail.message` is
 * NEVER read — every sentence the user sees comes from `SNIPPET_ERROR_COPY`
 * (or its two templated variants), keyed off `detail.code`.
 *
 * 403 is handled FIRST and keyed off the STATUS, not `detail` shape: both
 * the DELETE route's object-shaped `{code:"NOT_PERMITTED", required_slug}`
 * (§2.6) and the route-level bare-string refusal (bk-1177, every route)
 * resolve to the SAME ratified `NOT_PERMITTED` copy — the server's own
 * sentence is never rendered either way.
 *
 * A `422` pydantic body-shape error (`extra="forbid"`, missing `field`
 * query param handled separately by axios/FastAPI as its own 422) carries
 * `detail` as an ARRAY with no `code` — that falls through to the
 * `default` branch below and degrades to the generic `fallback`, never raw
 * JSON (§2.7 last-but-one row).
 */
function parseSnippetError(error, fallback) {
  if (error?.response?.status === 403) {
    const detail = error?.response?.data?.detail;
    const code = (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code)
      || SNIPPET_ERROR_CODE.NOT_PERMITTED;
    return { error: SNIPPET_ERROR_COPY[code] || SNIPPET_ERROR_COPY[SNIPPET_ERROR_CODE.NOT_PERMITTED], code };
  }
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, ...extras } = detail;
    delete extras.message;
    let copy;
    if (code === SNIPPET_ERROR_CODE.FIELD_TOO_LONG) copy = fieldTooLongCopy(extras);
    else if (code === SNIPPET_ERROR_CODE.SNIPPET_LIMIT_REACHED) copy = snippetLimitCopy(extras);
    else copy = SNIPPET_ERROR_COPY[code] || fallback;
    return { error: copy, code, ...extras };
  }
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 422: return { error: fallback, code: 'VALIDATION_ERROR' };
    default: return { error: fallback };
  }
}

/**
 * §2.4 GET …/discharge-snippets?field=<key>. `field` is a REQUIRED query
 * param (a canonical catalogue key). 200 -> `{field, owner_doctor_name,
 * limit, count, snippets}` (exactly 5 keys) — returned verbatim, this
 * client does not sort/filter/reshape (the server pre-sorts, §2.4).
 */
export const listDischargeSnippets = async (admissionId, field) => {
  try {
    const response = await apiClient.get(`${BASE}/${enc(admissionId)}/discharge-snippets`, {
      params: { field },
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[DischargeSnippetAPI] listDischargeSnippets error:', error);
    return { success: false, ...parseSnippetError(error, 'Could not load templates. Please try again.') };
  }
};

/**
 * §2.5 POST …/discharge-snippets. Body is EXACTLY `{field, title, text}`
 * (`extra="forbid"` on the server -> 422 otherwise). Read-only fields
 * (`snippet_id`, `field_key`, `created_by_name`, `created_at`,
 * `can_delete`, `doctor_member_id`, `created_by_member_id`,
 * `department_id`, `organization_id`) are NEVER accepted on the write
 * payload (§2.5, standing DoD #6) — this function only ever destructures
 * `field`/`title`/`text` off whatever the caller passes, so any read-only
 * key on the caller's object is silently dropped, never forwarded.
 *
 * 201 -> the flat snippet object, byte-identical 7-key shape to one
 * `snippets[]` entry of the GET (§2.4/§2.5) — one parser, one shape.
 */
export const createDischargeSnippet = async (admissionId, { field, title, text } = {}) => {
  try {
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/discharge-snippets`, {
      field, title, text,
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[DischargeSnippetAPI] createDischargeSnippet error:', error);
    return { success: false, ...parseSnippetError(error, 'Could not save this template. Please try again.') };
  }
};

/**
 * §2.6 DELETE …/discharge-snippets/{snippet_id}?field=<key>. `field` is a
 * REQUIRED query param (part of the SK; without it the row is
 * unaddressable). 200 -> `{deleted: true, snippet_id}`.
 */
export const deleteDischargeSnippet = async (admissionId, snippetId, field) => {
  try {
    const response = await apiClient.delete(
      `${BASE}/${enc(admissionId)}/discharge-snippets/${enc(snippetId)}`,
      { params: { field } },
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[DischargeSnippetAPI] deleteDischargeSnippet error:', error);
    return { success: false, ...parseSnippetError(error, 'Could not delete this template. Please try again.') };
  }
};

const dischargeSnippetApi = {
  listDischargeSnippets,
  createDischargeSnippet,
  deleteDischargeSnippet,
};

export default dischargeSnippetApi;
